import type {
  ApiSubscriptionId,
  ApiTabId,
  ApiTestTypeId,
  TestTabFilterRequestBody
} from '@api/fetchers/goal/test-tab/types';
import { TEST_SERIES_TAB } from '../Common/TestSeriesTabSwitcher';

import type {
  AllTestsCategoryTabId,
  CategoryTabId,
  FilterId,
  MyTestsCategoryTabId,
  TestSeriesView,
  UiSubscriptionId,
  UiTestTypeId
} from './types';

/** Kept in sync with `TestSeriesContentPanel` category tab ids */
export const CATEGORY_TAB = {
  UPCOMING: 'upcoming',
  ONGOING: 'ongoing',
  PREVIOUS_YEAR: 'previous_year',
  PAST: 'past',
  EDUCATOR: 'educator',
  ALL_SERIES: 'all_series',
  YOUR_UPCOMING: 'your_upcoming',
  YOUR_ATTEMPT: 'your_attempt'
} as const satisfies Record<string, CategoryTabId>;

export const API_TAB_ID = {
  UPCOMING: 'UPCOMING',
  PYT: 'PYT',
  PAST: 'PAST',
  EDUCATOR_TESTS: 'EDUCATOR_TESTS',
  TEST_SERIES: 'TEST_SERIES',
  MY_UPCOMING: 'MY_UPCOMING',
  MY_ATTEMPTS: 'MY_ATTEMPTS'
} as const satisfies Record<string, ApiTabId>;

export const API_SUBSCRIPTION = {
  FREE: '-1',
  PLUS: '1',
  ICONIC: '5'
} as const satisfies Record<string, ApiSubscriptionId>;

export const UI_SUBSCRIPTION = {
  PRIME: 'prime',
  PLUS: 'plus',
  ICONIC: 'iconic'
} as const satisfies Record<string, UiSubscriptionId>;

export const API_TO_UI_SUBSCRIPTION: Record<
  ApiSubscriptionId,
  UiSubscriptionId
> = {
  [API_SUBSCRIPTION.FREE]: UI_SUBSCRIPTION.PRIME,
  [API_SUBSCRIPTION.PLUS]: UI_SUBSCRIPTION.PLUS,
  [API_SUBSCRIPTION.ICONIC]: UI_SUBSCRIPTION.ICONIC
};

export const UI_TO_API_SUBSCRIPTION: Record<
  UiSubscriptionId,
  ApiSubscriptionId
> = {
  [UI_SUBSCRIPTION.PRIME]: API_SUBSCRIPTION.FREE,
  [UI_SUBSCRIPTION.PLUS]: API_SUBSCRIPTION.PLUS,
  [UI_SUBSCRIPTION.ICONIC]: API_SUBSCRIPTION.ICONIC
};

export const API_TEST_TYPE = {
  FULL: '1',
  MINOR: '2',
  PYT: '3'
} as const satisfies Record<string, ApiTestTypeId>;

export const UI_TEST_TYPE = {
  FULL_SYLLABUS: 'full_syllabus',
  MINOR_SYLLABUS: 'minor_syllabus',
  PREVIOUS_YEAR: 'previous_year'
} as const satisfies Record<string, UiTestTypeId>;

export const UI_TO_API_TEST_TYPE: Record<UiTestTypeId, ApiTestTypeId> = {
  [UI_TEST_TYPE.FULL_SYLLABUS]: API_TEST_TYPE.FULL,
  [UI_TEST_TYPE.MINOR_SYLLABUS]: API_TEST_TYPE.MINOR,
  [UI_TEST_TYPE.PREVIOUS_YEAR]: API_TEST_TYPE.PYT
};

export const API_TO_UI_TEST_TYPE = Object.entries(UI_TO_API_TEST_TYPE).reduce(
  (acc, [uiKey, apiVal]) => ({
    ...acc,
    [apiVal]: uiKey as UiTestTypeId
  }),
  {} as Record<ApiTestTypeId, UiTestTypeId>
);

const TEST_SYLLABUS_LABELS = {
  FULL_SYLLABUS: 'FULL SYLLABUS',
  MINOR_SYLLABUS: 'MINOR TEST',
  PYT: 'PREVIOUS YEAR TESTS'
} as const;

export const getSyllabusText = (programmeSubtype?: number): string => {
  if (programmeSubtype == null) {
    return TEST_SYLLABUS_LABELS.FULL_SYLLABUS;
  }

  switch (programmeSubtype - 1) {
    case 0:
      return TEST_SYLLABUS_LABELS.FULL_SYLLABUS;
    case 1:
      return TEST_SYLLABUS_LABELS.MINOR_SYLLABUS;
    case 2:
      return TEST_SYLLABUS_LABELS.PYT;
    default:
      return null;
  }
};

/** Filter pills shown per All Tests category subtab (order matters) */
export const CATEGORY_TAB_VISIBLE_FILTERS: Record<
  AllTestsCategoryTabId,
  FilterId[]
> = {
  [CATEGORY_TAB.UPCOMING]: ['subscription', 'educator', 'test_type'],
  [CATEGORY_TAB.ONGOING]: ['subscription', 'educator', 'test_type'],
  [CATEGORY_TAB.PREVIOUS_YEAR]: ['subscription', 'educator', 'status'],
  [CATEGORY_TAB.PAST]: ['subscription', 'educator', 'test_type'],
  [CATEGORY_TAB.EDUCATOR]: ['subscription', 'educator', 'test_type'],
  [CATEGORY_TAB.ALL_SERIES]: [
    'subscription',
    'educator',
    'test_type',
    'status',
    'enrollment'
  ]
};

/** My → Your upcoming: same filters as Upcoming */
export const MY_UPCOMING_VISIBLE_FILTERS: FilterId[] = [
  'subscription',
  'educator',
  'test_type'
];

export const getVisibleFiltersForCategoryTab = (
  categoryTabId: CategoryTabId
): FilterId[] => {
  if (categoryTabId === CATEGORY_TAB.YOUR_UPCOMING) {
    return MY_UPCOMING_VISIBLE_FILTERS;
  }

  if (categoryTabId === CATEGORY_TAB.YOUR_ATTEMPT) {
    return [];
  }

  return (
    CATEGORY_TAB_VISIBLE_FILTERS[categoryTabId as AllTestsCategoryTabId] || []
  );
};

const ALL_SUBSCRIPTIONS: ApiSubscriptionId[] = [
  API_SUBSCRIPTION.FREE,
  API_SUBSCRIPTION.PLUS,
  API_SUBSCRIPTION.ICONIC
];

const ALL_TEST_TYPES: ApiTestTypeId[] = [
  API_TEST_TYPE.FULL,
  API_TEST_TYPE.MINOR,
  API_TEST_TYPE.PYT
];

export const TAB_DEFAULT_FILTER_BODY: Record<
  ApiTabId,
  TestTabFilterRequestBody
> = {
  [API_TAB_ID.UPCOMING]: {
    schedule: [],
    educators: [],
    subscription: ALL_SUBSCRIPTIONS,
    test_type: ALL_TEST_TYPES,
    status: ['UPCOMING'],
    enrollment: []
  },
  [API_TAB_ID.PYT]: {
    schedule: [],
    educators: [],
    subscription: ALL_SUBSCRIPTIONS,
    test_type: [API_TEST_TYPE.PYT],
    status: ['LIVE', 'PAST', 'UPCOMING'],
    enrollment: []
  },
  [API_TAB_ID.PAST]: {
    schedule: [],
    educators: [],
    subscription: ALL_SUBSCRIPTIONS,
    test_type: ALL_TEST_TYPES,
    status: ['PAST'],
    enrollment: []
  },
  [API_TAB_ID.EDUCATOR_TESTS]: {
    schedule: [],
    educators: [],
    subscription: ALL_SUBSCRIPTIONS,
    test_type: ALL_TEST_TYPES,
    status: ['LIVE', 'PAST', 'UPCOMING'],
    enrollment: []
  },
  [API_TAB_ID.TEST_SERIES]: {
    schedule: [],
    educators: [],
    subscription: ALL_SUBSCRIPTIONS,
    test_type: ALL_TEST_TYPES,
    status: ['COMPLETED', 'ONGOING', 'UPCOMING'],
    enrollment: ['ENROLLED', 'NOT_ENROLLED']
  },
  [API_TAB_ID.MY_UPCOMING]: {
    schedule: [],
    educators: [],
    subscription: ALL_SUBSCRIPTIONS,
    test_type: ALL_TEST_TYPES,
    status: [],
    enrollment: []
  },
  [API_TAB_ID.MY_ATTEMPTS]: {
    schedule: [],
    educators: [],
    subscription: ALL_SUBSCRIPTIONS,
    test_type: ALL_TEST_TYPES,
    status: [],
    enrollment: []
  }
};

/** Status overrides for web-only category tabs that share a POST `id` */
export const WEB_TAB_STATUS_OVERRIDE: Partial<
  Record<AllTestsCategoryTabId, TestTabFilterRequestBody['status']>
> = {
  [CATEGORY_TAB.ONGOING]: ['ONGOING']
};

export const getApiTabId = (
  view: TestSeriesView,
  categoryTabId: CategoryTabId
): ApiTabId | null => {
  if (view === TEST_SERIES_TAB.MY) {
    if (categoryTabId === CATEGORY_TAB.YOUR_UPCOMING) {
      return API_TAB_ID.MY_UPCOMING;
    }

    if (categoryTabId === CATEGORY_TAB.YOUR_ATTEMPT) {
      return API_TAB_ID.MY_ATTEMPTS;
    }

    return null;
  }

  const map: Record<AllTestsCategoryTabId, ApiTabId> = {
    [CATEGORY_TAB.UPCOMING]: API_TAB_ID.UPCOMING,
    [CATEGORY_TAB.ONGOING]: API_TAB_ID.UPCOMING,
    [CATEGORY_TAB.PREVIOUS_YEAR]: API_TAB_ID.PYT,
    [CATEGORY_TAB.PAST]: API_TAB_ID.PAST,
    [CATEGORY_TAB.EDUCATOR]: API_TAB_ID.EDUCATOR_TESTS,
    [CATEGORY_TAB.ALL_SERIES]: API_TAB_ID.TEST_SERIES
  };

  return map[categoryTabId as AllTestsCategoryTabId] || API_TAB_ID.UPCOMING;
};

export const isMyAttemptsTab = (
  view: TestSeriesView,
  categoryTabId: CategoryTabId
): boolean =>
  view === TEST_SERIES_TAB.MY &&
  categoryTabId === (CATEGORY_TAB.YOUR_ATTEMPT as MyTestsCategoryTabId);

export const showFiltersForTab = (
  view: TestSeriesView,
  categoryTabId: CategoryTabId
): boolean => !isMyAttemptsTab(view, categoryTabId);

/** User-context tabs/endpoints that must not be fetched for logged-out users */
export const requiresAuthForTestTab = (
  view: TestSeriesView,
  categoryTabId: CategoryTabId,
  apiTabId: ApiTabId | null
): boolean =>
  view === TEST_SERIES_TAB.MY ||
  apiTabId === API_TAB_ID.MY_UPCOMING ||
  isMyAttemptsTab(view, categoryTabId);

export const LIST_PAGE_SIZE = 50;
